import React, { useEffect } from "react";
import { useNetwork } from "context/NetworkProvider";

const FullPageOffline = () => {
  const { isReconnecting, retry } = useNetwork();

  // Lock ESC key — fully locked state
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") e.preventDefault();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <div
      role="alert"
      aria-live="assertive"
      aria-label="You are offline"
      className="fixed inset-0 z-[99999] flex items-center justify-center overflow-hidden animate-fadeIn backdrop-blur-md bg-white/30"
    >
      {/* Main Card */}
      <div className="relative bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl px-8 py-12 sm:px-14 sm:py-16 max-w-md w-[90%] text-center transition-all duration-300 border border-white/50">

        {/* Connection status dot */}
        <div className="absolute top-5 right-5 flex items-center gap-2">
          <span
            className={`block w-2.5 h-2.5 rounded-badge transition-colors duration-300 ${
              isReconnecting ? "bg-green-400 animate-pulse" : "bg-red-500"
            }`}
          />
          <span className="text-xs text-content-tertiary font-medium">
            {isReconnecting ? "Reconnecting..." : "Offline"}
          </span>
        </div>

        {/* Floating Illustration */}
        <div className="mx-auto mb-8 w-28 h-28 animate-float">
          <svg viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full drop-shadow-md">
            {/* Cloud body */}
            <ellipse cx="60" cy="68" rx="38" ry="22" fill="#E2E8F0" />
            <ellipse cx="44" cy="56" rx="20" ry="18" fill="#E2E8F0" />
            <ellipse cx="72" cy="52" rx="24" ry="22" fill="#E2E8F0" />
            <ellipse cx="56" cy="48" rx="18" ry="16" fill="#F1F5F9" />

            {/* WiFi slash icon */}
            <g transform="translate(40, 55)">
              <path d="M20 22a2 2 0 100-4 2 2 0 000 4z" fill="#94A3B8" />
              <path d="M8.5 11.5a16.1 16.1 0 0123 0" stroke="#94A3B8" strokeWidth="2.5" strokeLinecap="round" fill="none" />
              <path d="M13 16a9.5 9.5 0 0114 0" stroke="#94A3B8" strokeWidth="2.5" strokeLinecap="round" fill="none" />
              {/* Slash line */}
              <line x1="6" y1="4" x2="34" y2="26" stroke="#EF4444" strokeWidth="2.5" strokeLinecap="round" />
            </g>
          </svg>
        </div>

        {/* Heading */}
        <h1 className="text-2xl sm:text-3xl font-bold text-content-primary tracking-tight mb-3">
          You're Offline
        </h1>

        {/* Subtext */}
        <p className="text-sm sm:text-base text-content-secondary leading-relaxed mb-8 max-w-xs mx-auto">
          Please check your internet connection. The system will automatically reconnect.
        </p>

        {/* Retry Button */}
        <button
          onClick={retry}
          disabled={isReconnecting}
          aria-label="Retry connection"
          className={`
            inline-flex items-center justify-center gap-2 px-7 py-3 rounded-card
            text-sm font-semibold text-white transition-all duration-200
            focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500
            ${isReconnecting
              ? "bg-gray-300 cursor-not-allowed"
              : "bg-brand-500 hover:bg-[#6B5CE7] active:scale-[0.97] shadow-lg shadow-brand-500/25"
            }
          `}
        >
          {isReconnecting ? (
            <>
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Reconnecting...
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Try Again
            </>
          )}
        </button>

        {/* Bottom subtle hint */}
        <p className="mt-6 text-xs text-content-tertiary">
          Vistaar
        </p>
      </div>
    </div>
  );
};

export default FullPageOffline;
